<template>
  <div>
    <div v-show="numberNewLeads" class="LIST-HEADER">
      <div class="d-flex">
        <div>
          <vk-icon
              name="information-filled"
              class="m-r-1 ICON-GOLD"
              size="20"/>
        </div>
        <div>
          <span>You have {{newLeads}} that will expire in{{newLeadExpire}}.</span>
          <router-link
              :to="{
                name: 'dashboard::pro::jobs',
                params: {
                  jobsStatusAlias: 'new',
                },
              }"
              class="link"> Contact them now
        </router-link></div>
      </div>

    </div>
    <div class="LIST" v-if="actionItems.length">
      <div class="p-b-2">
        <action-item
            v-for="(item, index) in actionItems"
            :key="index"
            :message="item.message"
            :index="index"
        />
      </div>
    </div>
    <div v-else >
      <div class="d-flex fd-column jc-around">
        <img
            class="IMAGE"
            src="./assets/emptystate_handsaw.svg"
        />
        <div v-if="noData" class="p-x-4 p-x-1:mo p-b-5 text-md text-center">
          You don’t have any performance data from the last 90 days yet.
          Keep an eye on this section for improvement suggestions and helpful tips!
        </div>
        <div v-else class="p-x-4 p-x-1:mo p-b-5 text-md text-center">
          <p class="text-bold">
            Keep it up!
          </p>
          <p>Your performance from the last 90 days is right on track.
          Check back another time for improvement suggestions and helpful tips.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { ACTION_ITEMS } from '@/constants/action-items';
import addUTCTimeZoneDesignator from '@/utils/add-utc-time-zone-designator';
import ActionItem from './ActionItem';

export default {
  ACTION_ITEMS,
  name: 'action-item-list',
  components: { ActionItem },
  props: {
    ninetyDays: {
      type: Object,
      required: true,
    },
    totalReviews: {
      type: [Number, String],
      required: true,
    },
    pointsBalance: {
      type: [Number, String],
      required: true,
    },
    averageRating: {
      type: [Number, String],
      required: true,
    },
    isTopPro: {
      type: [Boolean],
      default: null,
      required: false,
    },
    watchedVideos: {
      type: Number,
      default: 0,
      required: false,
    },
  },
  data () {
    return {
      actionItems: [],
      numberNewLeads: 0,
      oldestLeadTime: 0,
      ACTION_ITEMS_CONSTANTS: this.$options.ACTION_ITEMS(
        this.$runtimeConstants.SALESFORCE_ACCOUNT_REDIRECT_DOMAIN),
    };
  },
  computed: {
    noData () {
      return this.ninetyDays.total_leads < 1;
    },
    hasRecentReviewAndHealthyRating () {
      return Number(this.totalReviews) > 0 && Number(this.averageRating) >= 3.9;
    },
    showFooter () {
      return this.numberNewLeads && this.numberNewLeads;
    },
    newLeads () {
      const leads = this.numberNewLeads > 1
        ? ` ${this.numberNewLeads} new leads `
        : ` ${this.numberNewLeads} new lead `;
      return leads;
    },
    newLeadExpire () {
      const hourDifference = this.hoursFromNow(this.oldestLeadTime) || null;
      return hourDifference > 1 ? ` ${hourDifference} hours` : ` ${hourDifference} hour`;
    },
    watchedVideosMessage () {
      return `<a class="text-bold" href="/account/education">LINK to Pro Education </a><span>Our records show you have only watched ${this.watchedVideos} videos. There are many more videos to watch about Pro Referral.</span>`;
    },
  },
  created () {
    if (!this.noData) {
      this.getNewLeads();
    }
  },
  mounted () {
    this.addActionItems();
  },
  methods: {
    ...mapActions('dashboard/workOrderMatches', [
      'fetchWorkOrderMatches',
    ]),

    hoursFromNow (date) {
      const diffInMs = Math.abs(new Date() - new Date(addUTCTimeZoneDesignator(date)));
      const hourDifference = 48 - Math.ceil(diffInMs / (1000 * 60 * 60));
      return hourDifference;
    },
    async getNewLeads () {
      const { entities } = await this.fetchWorkOrderMatches({
        params: {
          statuses: 'unreplied',
          size: 100,
          service_provider_id: this.serviceProviderId,
          sort: 'time_created asc',
        },
      });
      if (entities.proWorkOrdersMatches) {
        const jobDates = Object.entries(entities.proWorkOrdersMatches)
          .filter((item) => {
            return this.hoursFromNow(item[1].time_created) > 0;
          })
          .map(item => item[1].time_created);
        this.oldestLeadTime = addUTCTimeZoneDesignator(jobDates[0]);
        this.numberNewLeads = jobDates.length;
      }
    },
    addActionItems () {
      if (!this.noData) {
        if (this.isTopPro !== null && this.isTopPro === false) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.NOT_TOP_PRO);
        }
        if (this.ninetyDays.response_rate <= 79) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.RESPONSE_BELOW);
        }
        if (this.ninetyDays.response_rate >= 80 && this.ninetyDays.leads_hired === 0) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.RESPONSE_ABOVE_NO_HIRED);
        }
        if (this.ninetyDays.leads_passed / this.ninetyDays.total_leads > 0.30) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.PASS_LEADS_HIGH);
        }
        if (this.ninetyDays.leads_ignored / this.ninetyDays.total_leads > 0.20) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.IGNORED_HIGH);
        }
        if (this.ninetyDays.avg_contact_time > 36) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.RESPONSE_TIME_HIGH);
        }
        if (this.totalReviews < 5 && !this.hasRecentReviewAndHealthyRating) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.TOO_FEW_REVIEWS);
        } else if (
          this.averageRating <= 3.9
          && this.totalReviews > 0
          && !this.hasRecentReviewAndHealthyRating
        ) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.LOW_REVIEW_SCORE);
        }
        if (this.pointsBalance < 1500) {
          this.actionItems.push(this.ACTION_ITEMS_CONSTANTS.LOW_POINTS_BALANCE);
        }
        if (this.watchedVideos < 3) {
          this.actionItems.push({ message: this.watchedVideosMessage });
        }
      }
    },
  },
};

</script>

<style lang="scss" scoped>
@import "~@homeservices/design-kit/tools";

.LIST {
  padding-bottom: 8 * $unit;
}
.LIST-HEADER{
    width: 100%;
    color: $color-gray80;
    background-color: #FFFBE6;
    border-radius: 2px;
    font-size: 14px;
    padding: 12px 32px;
    border: solid 1px #FFE58F;
    a.link {
  text-decoration: none;
  font-weight: 600;
  }
}
/deep/.LIST-LINK:link {
    color:#FFFFFF;
    text-decoration: underline;
}
.IMAGE {
  align-self: center;
  padding: 15.5 *$unit 3.5 *$unit 5 *$unit;

  @include mobile-only {
    padding: 5 *$unit 3.5 *$unit 3 *$unit;
  }
}
.ICON-GOLD {
  color: #FAAD14;
}

</style>
